import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { LOCALE, ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getKindDefault } from "~/common/defaults/kind.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { kindNormalize } from "~/common/helpers/kind-adapter";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { CreateKindDto } from "~/common/interfaces/dto/kinds/create-kind.dto";
import type { GetAllKindsDto } from "~/common/interfaces/dto/kinds/get-all-kinds.dto";
import type { UpdateKindByIdDto } from "~/common/interfaces/dto/kinds/update-kind-by-id.dto";
import type { Kind, KindAdapted } from "~/common/interfaces/entities/kind.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useKindsStore = defineStore(
	"kinds",
	() => {
		const { locale } = useI18n();
		const kindNormalizeWithLocale = kindNormalize.bind(this, locale as Ref<LOCALE>);

		const currentKind = ref<KindAdapted>(getKindDefault());
		const kinds = ref<KindAdapted[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllKindsDto & PaginationDto>();
		const tableSort = ref<Sort>({
			columns: [],
			orderBy: ["createdAt", ORDER_BY.DESC]
		});

		const createOne = async (dto: CreateKindDto): Promise<KindAdapted> => {
			const kind = await useNuxtApp().$apiFetch<Kind>("/kinds", {
				method: "POST",
				body: dto
			});

			return kindNormalizeWithLocale(kind);
		};
		const getAll = async (
			dto: GetAllKindsDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<KindAdapted>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Kind>>("/kinds", {
				query: recentGetAllQuery.value
			});
			kinds.value = res.data.map(kind => kindNormalizeWithLocale(kind));
			meta.value = res.meta;

			return res as PaginationResponseDto<KindAdapted>;
		};
		const getById = async (id: number): Promise<KindAdapted> => {
			const kind = await useNuxtApp().$apiFetch<Kind>(`/kinds/${id}`);

			return kindNormalizeWithLocale(kind);
		};
		const patchById = async (id: number, dto: UpdateKindByIdDto): Promise<KindAdapted> => {
			const kind = await useNuxtApp().$apiFetch<Kind>(`/kinds/${id}`, {
				method: "PATCH",
				body: dto
			});

			return kindNormalizeWithLocale(kind);
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/kinds/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentKind,
			kinds,
			meta,
			recentGetAllQuery,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
